/**
   * section-cart-drawer.js
   * Interactive AJAX cart drawer functionality for GOZO theme.
   */

(function() {
  'use strict';

  // Open the cart drawer
  function openCartDrawer() {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  }

  // Close the cart drawer
  function closeCartDrawer() {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = ''; // Restore background scrolling
  }

  // Re-read cart badges on page and update their inner text and visibility
  function updateCartBadges() {
    const drawerCountEl = document.querySelector('[data-cart-drawer-count]');
    if (!drawerCountEl) return;

    // Extract current count from drawer count element (e.g. "(3)" -> 3)
    const countMatch = drawerCountEl.textContent.match(/\d+/);
    const count = countMatch ? parseInt(countMatch[0], 10) : 0;

    // 1. Update skeleton header: <sup> tag inside cart link
    // Look for <a href="/cart">
    const headerCartLinks = document.querySelectorAll('a[href="/cart"]:not(.cart-drawer__cart-btn), a[href$="/cart"]:not(.cart-drawer__cart-btn)');
    
    headerCartLinks.forEach(link => {
      // Find existing badge
      let badge = link.querySelector('.nav-bar__badge, .gozo-mobile-nav__cart-count');
      
      // If badge doesn't exist but count > 0, dynamically create it in the wrapper
      if (!badge && count > 0) {
        const wrap = link.querySelector('.nav-bar__cart-wrap');
        if (wrap) {
          badge = document.createElement('span');
          if (link.classList.contains('gozo-mobile-nav__cart')) {
            badge.className = 'gozo-mobile-nav__cart-count';
          } else {
            badge.className = 'nav-bar__badge';
            if (link.classList.contains('nav-bar__mobile-action')) {
              badge.classList.add('nav-bar__mobile-badge');
            } else {
              badge.setAttribute('aria-hidden', 'true');
            }
          }
          wrap.appendChild(badge);
        }
      }

      if (badge) {
        if (count > 0) {
          badge.textContent = count;
          badge.style.display = '';
        } else {
          badge.style.display = 'none';
        }
      }

      // Default header sup badge: sup tag (for Skeleton theme fallback)
      let supBadge = link.querySelector('sup');
      if (supBadge) {
        if (count > 0) {
          supBadge.textContent = count;
          supBadge.style.display = '';
        } else {
          supBadge.style.display = 'none';
        }
      } else if (count > 0) {
        // If sup doesn't exist but we have items, create one (only for Skeleton header)
        if (!link.classList.contains('nav-bar__action-link') && !link.classList.contains('nav-bar__mobile-action') && !link.classList.contains('gozo-mobile-nav__cart')) {
          const sup = document.createElement('sup');
          sup.textContent = count;
          link.insertBefore(sup, link.firstChild);
        }
      }
    });
  }

  // Send request to Shopify Ajax API to change item quantity
  function updateQuantity(key, quantity) {
    const drawer = document.getElementById('CartDrawer');
    if (drawer) drawer.classList.add('is-loading');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity: quantity
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(cart => {
      return refreshCartDrawer();
    })
    .catch(error => {
      console.error('Error updating quantity in cart:', error);
    })
    .finally(() => {
      if (drawer) drawer.classList.remove('is-loading');
    });
  }

  // Refresh cart drawer markup using Section Rendering API
  function refreshCartDrawer() {
    // Request the cart-drawer section rendering
    return fetch('/?sections=cart-drawer')
      .then(response => {
        if (!response.ok) throw new Error('Could not fetch section HTML');
        return response.json();
      })
      .then(data => {
        const html = data['cart-drawer'];
        if (!html) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newInner = doc.querySelector('.cart-drawer__inner');
        const currentInner = document.querySelector('.cart-drawer__inner');

        if (newInner && currentInner) {
          // Replace content inside drawer
          currentInner.innerHTML = newInner.innerHTML;
          
          // Rebind actions for elements inside newly rendered drawer
          bindDrawerActions();

          // Sync badges with newly rendered count
          updateCartBadges();
        }
      })
      .catch(error => {
        console.error('Error refreshing cart drawer:', error);
      });
  }

  // Bind event listeners for actions inside the cart drawer
  function bindDrawerActions() {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    // Bind close buttons (multiple can exist: header close button, empty state button, etc.)
    const closeButtons = drawer.querySelectorAll('[data-cart-drawer-close]');
    closeButtons.forEach(btn => {
      btn.removeEventListener('click', closeCartDrawer);
      btn.addEventListener('click', closeCartDrawer);
    });

    // Bind item actions (stepper increment/decrement and remove button)
    const actionButtons = drawer.querySelectorAll('.cart-drawer-item__qty-btn, .cart-drawer-item__remove');
    actionButtons.forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        const key = this.dataset.key;
        const qty = parseInt(this.dataset.qty, 10);
        updateQuantity(key, qty);
      });
    });
  }

  // Intercept all clicks to cart links on the page and open the drawer instead
  function interceptCartLinks() {
    document.addEventListener('click', function(e) {
      // Find closest link tag
      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      // Intercept if it's the cart URL and NOT the checkout link or similar
      if (href === '/cart' || href === window.Shopify?.routes?.root + 'cart') {
        if (link.closest('#CartDrawer')) return;
        // Double check if drawer element is in DOM
        const drawer = document.getElementById('CartDrawer');
        if (drawer) {
          e.preventDefault();
          openCartDrawer();
        }
      }
    });
  }

  // Intercept product form submissions (Add to Cart button) and submit via AJAX
  function interceptProductForms() {
    document.addEventListener('submit', function(e) {
      const form = e.target;
      
      // Look for forms targeting the cart/add endpoint
      if (form.action && (form.action.includes('/cart/add') || form.action.endsWith('/add'))) {
        e.preventDefault();

        const submitBtn = form.querySelector('[type="submit"], [name="add"]');
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.classList.add('is-loading');
        }

        const formData = new FormData(form);

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        })
        .then(response => {
          if (!response.ok) throw new Error('Add to cart failed');
          return response.json();
        })
        .then(item => {
          // Success! Refresh cart drawer contents and then open it
          return refreshCartDrawer().then(() => {
            openCartDrawer();
          });
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          // If fallback fails, submit the form normally
          form.submit();
        })
        .finally(() => {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-loading');
          }
        });
      }
    });
  }

  // Setup initial hooks
  function initCartDrawer() {
    bindDrawerActions();
    interceptCartLinks();
    interceptProductForms();
  }

  // DOMContentLoaded loader
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawer);
  } else {
    initCartDrawer();
  }

  // Re-initialize inside Shopify customizer if re-rendered
  document.addEventListener('shopify:section:load', function(e) {
    if (e.target.classList.contains('cart-drawer') || e.target.querySelector('#CartDrawer')) {
      bindDrawerActions();
    }
  });

})();
